<template>
  <!-- Overlay -->
  <transition name="fade-overlay">
    <div
      v-if="cart.isOpen"
      class="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm"
      @click="cart.closeCart()"
    />
  </transition>

  <!-- Drawer -->
  <transition name="slide-cart">
    <div
      v-if="cart.isOpen"
      class="fixed top-0 right-0 h-full w-full max-w-md z-50 flex flex-col"
      style="background: rgba(12,12,12,0.97); border-left: 1px solid rgba(255,215,0,0.15);"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-5 border-b border-white/10">
        <div>
          <h2 class="text-2xl font-black text-white uppercase tracking-widest">Panier</h2>
          <p class="text-fire-gold text-sm font-bold">{{ cart.itemCount }} article{{ cart.itemCount > 1 ? 's' : '' }}</p>
        </div>
        <button
          @click="cart.closeCart()"
          class="p-2 rounded-full border border-white/10 hover:border-fire-gold hover:text-fire-gold transition-all text-white"
        >
          <XMarkIcon class="w-6 h-6" />
        </button>
      </div>

      <!-- Weight Info -->
      <div v-if="cart.items.length > 0" class="px-6 py-4 bg-fire-gold/5 border-b border-white/5">
        <div class="flex justify-between items-center">
          <div class="flex flex-col">
            <span class="text-[10px] font-black uppercase tracking-widest text-white/50">Estimation Livraison</span>
            <span class="text-fire-gold font-black">{{ cart.shippingName }} ({{ cart.shippingCost.toFixed(2) }}€)</span>
          </div>
          <div class="text-right">
            <span class="text-[10px] font-black uppercase tracking-widest text-white/50">Poids Total</span>
            <p class="text-white font-black">{{ (cart.totalWeight / 1000).toFixed(2) }} kg</p>
          </div>
        </div>
      </div>

      <!-- Items list -->
      <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        <div v-if="cart.items.length === 0" class="flex flex-col items-center justify-center h-full text-center gap-4">
          <ShoppingCartIcon class="w-16 h-16 text-white/10" />
          <p class="text-gray-500 font-bold">Votre panier est vide.</p>
          <button @click="cart.closeCart()" class="btn-primary mt-4">Continuer mes achats</button>
        </div>

        <TransitionGroup name="cart-item" tag="div" class="space-y-4">
          <div
            v-for="item in cart.items"
            :key="item.id"
            class="flex gap-4 items-center p-4 rounded-2xl"
            style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06);"
          >
            <img :src="item.image" :alt="item.name" class="w-16 h-16 object-cover rounded-xl flex-shrink-0" />

            <div class="flex-1 min-w-0">
              <h3 class="text-white font-bold text-sm leading-tight uppercase truncate">{{ item.name }}</h3>
              <p class="text-fire-gold font-black mt-1">{{ (item.price * item.quantity).toFixed(2) }}€</p>

              <!-- Quantity controls -->
              <div class="flex items-center gap-2 mt-2">
                <button
                  @click="cart.updateQty(item.id, item.quantity - 1)"
                  class="w-7 h-7 rounded-full border border-white/20 flex items-center justify-center hover:border-fire-gold hover:text-fire-gold transition-all text-white text-lg font-bold leading-none"
                >−</button>
                <span class="text-white font-black w-6 text-center">{{ item.quantity }}</span>
                <button
                  @click="cart.updateQty(item.id, item.quantity + 1)"
                  class="w-7 h-7 rounded-full border border-white/20 flex items-center justify-center hover:border-fire-gold hover:text-fire-gold transition-all text-white text-lg font-bold leading-none"
                >+</button>
              </div>
            </div>

            <button
              @click="cart.removeItem(item.id)"
              class="p-1.5 text-gray-500 hover:text-red-400 transition-colors flex-shrink-0"
            >
              <TrashIcon class="w-5 h-5" />
            </button>
          </div>
        </TransitionGroup>
      </div>

      <!-- Footer / Total -->
      <div v-if="cart.items.length > 0" class="px-6 py-6 border-t border-white/10 space-y-4">
        <div class="space-y-2 border-b border-white/5 pb-4">
          <div class="flex justify-between items-center text-sm">
            <span class="text-gray-400 font-bold uppercase tracking-widest">Sous-total</span>
            <span class="text-white font-bold">{{ cart.total.toFixed(2) }}€</span>
          </div>
          <div class="flex justify-between items-center text-sm">
            <span class="text-gray-400 font-bold uppercase tracking-widest">Livraison ({{ cart.shippingName }})</span>
            <span class="text-white font-bold">{{ cart.shippingCost.toFixed(2) }}€</span>
          </div>
        </div>
        <div class="flex justify-between items-center pt-2">
          <span class="text-gray-400 font-bold uppercase tracking-widest text-sm">Total</span>
          <span class="text-3xl font-black text-white">{{ (cart.total + cart.shippingCost).toFixed(2) }}€</span>
        </div>
        <button 
          @click="handleCheckout"
          class="btn-primary w-full text-center text-lg py-4 font-black uppercase tracking-widest"
        >
          Passer la commande
        </button>
        <button
          @click="cart.clearCart()"
          class="w-full text-center text-sm text-gray-600 hover:text-red-400 transition-colors font-bold py-1"
        >
          Vider le panier
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { useCartStore } from '../stores/cart';
import { useAuthStore } from '../stores/auth';
import { XMarkIcon, ShoppingCartIcon, TrashIcon } from '@heroicons/vue/24/solid';

const cart = useCartStore();
const auth = useAuthStore();
const router = useRouter();

const handleCheckout = () => {
  if (auth.isAuthenticated) {
    cart.closeCart();
    router.push('/checkout');
  } else {
    auth.showLoginModal = true;
  }
};

const onAuthSuccess = () => {
  cart.closeCart();
  router.push('/checkout');
};
</script>

<style scoped>
.fade-overlay-enter-active,
.fade-overlay-leave-active { transition: opacity 0.3s ease; }
.fade-overlay-enter-from,
.fade-overlay-leave-to { opacity: 0; }

.slide-cart-enter-active,
.slide-cart-leave-active { transition: transform 0.4s cubic-bezier(0.19, 1, 0.22, 1); }
.slide-cart-enter-from,
.slide-cart-leave-to { transform: translateX(100%); }

.cart-item-enter-active { transition: all 0.3s ease; }
.cart-item-leave-active { transition: all 0.25s ease; position: absolute; }
.cart-item-enter-from { opacity: 0; transform: translateX(30px); }
.cart-item-leave-to { opacity: 0; transform: translateX(-30px); }
.cart-item-move { transition: transform 0.3s ease; }
</style>
